import { apiClient, isSupabaseConfigured, getActivePvzId, clearCache } from './_shared.js'
import { sendBulkNotifications } from './tasks.js'
import { autoCloseExpiredShifts } from './schedule.js'

// =============================================
// Block 21: SUPPLY REQUESTS API
// =============================================

export async function getSupplyRequests(pvzId, orgId) {
  if (!isSupabaseConfigured) return []
  const params = new URLSearchParams()
  if (pvzId) params.append('pvzId', pvzId)
  if (orgId) params.append('orgId', orgId)
  return await apiClient(`/supply-requests?${params.toString()}`)
}

export async function getPendingSupplyRequestsCount(orgId, pvzId) {
  if (!isSupabaseConfigured || !orgId) return 0
  const params = new URLSearchParams()
  params.append('orgId', orgId)
  if (pvzId) params.append('pvzId', pvzId)
  const data = await apiClient(`/supply-requests/pending-count?${params.toString()}`)
  return data.count || 0
}

export async function getOwnerActiveSuppliesCount(orgId, pvzId) {
  if (!isSupabaseConfigured || !orgId) return 0
  const params = new URLSearchParams()
  params.append('orgId', orgId)
  if (pvzId) params.append('pvzId', pvzId)
  const data = await apiClient(`/supply-requests/active-count?${params.toString()}`)
  return data.count || 0
}

export async function getLastSupplyPaymentDetails(employeeId) {
  if (!isSupabaseConfigured || !employeeId) return ''
  const data = await apiClient(`/supply-requests/last-payment-details?employeeId=${employeeId}`)
  return data.details && data.details.length > 0 ? data.details[0] : ''
}

export async function getLastSupplyPaymentDetailsHistory(employeeId) {
  if (!isSupabaseConfigured || !employeeId) return []
  const data = await apiClient(`/supply-requests/last-payment-details?employeeId=${employeeId}`)
  return data.details || []
}

export async function createSupplyRequest(requestData) {
  if (!isSupabaseConfigured) return null
  const data = await apiClient('/supply-requests', {
    method: 'POST',
    body: JSON.stringify(requestData)
  })
  clearCache('supply_requests')
  return data
}

export async function updateSupplyRequest(id, requestData) {
  if (!isSupabaseConfigured) return null
  const data = await apiClient(`/supply-requests/${id}`, {
    method: 'PUT',
    body: JSON.stringify(requestData)
  })
  clearCache('supply_requests')
  return data
}

export async function deleteSupplyRequest(id) {
  if (!isSupabaseConfigured) return null
  await apiClient(`/supply-requests/${id}`, { method: 'DELETE' })
  clearCache('supply_requests')
  return true
}

export async function notifyAdminsAboutSupplyRequest(orgId, title, employeeName) {
  try {
    const allEmps = await apiClient(`/employees?orgId=${orgId}`)
    const admins = allEmps.filter(e => ['owner', 'admin', 'admin_employee'].includes(e.role))
    if (!admins || admins.length === 0) return

    const notifications = admins.map(admin => ({
      profile_id: admin.id,
      type: 'supply_request_new',
      title: 'Новая заявка на хознужды',
      message: `${employeeName} создал заявку: "${title}"`,
      is_read: false,
    }))

    await sendBulkNotifications(notifications)
  } catch(e) {
    console.warn('Admins supply notification error:', e)
  }
}

export async function notifyEmployeeAboutSupplyRequest(employeeId, title, status) {
  if (!isSupabaseConfigured) return
  try {
    const statusLabels = {
      approved: 'одобрена',
      paid: 'оплачена',
      declined: 'отклонена',
      completed: 'выполнена'
    }
    const label = statusLabels[status] || status
    await apiClient('/notifications', {
      method: 'POST',
      body: JSON.stringify({
        profile_id: employeeId,
        type: 'supply_request_update',
        title: 'Обновление заявки на хознужды',
        message: `Ваша заявка "${title}" была ${label}`
      })
    })
  } catch(e) {
    console.warn('Employee supply notification error:', e)
  }
}

export async function notifyEmployeeAboutSupplyComment(employeeId, title, commentText) {
  if (!isSupabaseConfigured) return
  try {
    await apiClient('/notifications', {
      method: 'POST',
      body: JSON.stringify({
        profile_id: employeeId,
        type: 'supply_request_comment',
        title: 'Комментарий руководства к заявке',
        message: `К вашей заявке "${title}" добавлен комментарий: "${commentText.slice(0, 100)}"`
      })
    })
  } catch(e) {
    console.warn('Employee comment notification error:', e)
  }
}

export async function getEmployeeShiftsMarkers(employeeId, employeeName) {
  const pvzId = getActivePvzId()
  // Закрываем зависшие смены
  await autoCloseExpiredShifts(pvzId)

  const start = new Date()
  start.setDate(start.getDate() - 60)
  const startStr = start.toISOString().split('T')[0]
  
  const end = new Date()
  end.setDate(end.getDate() + 30)
  const endStr = end.toISOString().split('T')[0]

  const data = await apiClient(`/schedule?employeeId=${employeeId}&startDate=${startStr}&endDate=${endStr}&pvzId=${pvzId}`)

  const markers = {}
  ;(data || []).forEach(s => {
    const hasWorked = (parseFloat(s.hours) || 0) > 0 || s.started_at
    if (hasWorked) {
      markers[s.work_date] = [
        { id: employeeId, name: employeeName, filled: true }
      ]
    }
  })
  return markers
}
